'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useScrollSpy } from '@/hooks/use-scroll-spy';
import { cn } from '@/lib/utils';
import { Code2 } from 'lucide-react';

const navItems = [
  { id: 'home', label: 'Home' },
  { id: 'resume', label: 'Resume' },
  { id: 'skills', label: 'Skills' },
  { id: 'projects', label: 'Projects' },
  { id: 'chat', label: 'Chat' },
  { id: 'contact', label: 'Contact' },
];

export default function Header({ sectionIds }: { sectionIds: string[] }) {
  const [hasScrolled, setHasScrolled] = useState(false);
  const activeSection = useScrollSpy(sectionIds, 100);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    document.querySelector(targetId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    // Update URL hash without jumping
    if (history.pushState) {
        history.pushState(null, '', targetId);
    } else {
        window.location.hash = targetId;
    }
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        hasScrolled ? 'bg-background/80 shadow-md backdrop-blur-sm' : 'bg-transparent'
      )}
    >
      <div className="container mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="#home" onClick={(e) => handleNavClick(e, '#home')} className="relative z-10">
          <Image 
            src="/logo_trans.png" 
            alt="mvrey"
            width={60}
            height={60}
            className="transition-all duration-300 ease-in-out hover:scale-110"
            priority
          />
        </a>
        <nav className="hidden md:flex items-center space-x-6">
          {navItems.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => handleNavClick(e, `#${item.id}`)}
              className={cn(
                'text-sm font-medium transition-colors hover:text-primary',
                activeSection === item.id ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              {item.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
}
